import Decimal from "decimal.js"
import PropTypes from "prop-types"
import React, { useContext } from "react"
import { POSContext } from "src/main/PointOfSale/Layout"

import Heading from "src/components/Heading"

import useWindowSize from "src/hooks/use_window_size"

import DockwaPayModal from "../DockwaPayModal"
import PanelWrapper from "../PanelWrapper"
import PaymentMethodButton from "./PaymentMethodButton"
import { CheckoutContext } from "./index"
import { Cash } from "./payment_flows/cash"
import { Check } from "./payment_flows/check"
import { CreditCard } from "./payment_flows/credit_card"
import { DockwaPay } from "./payment_flows/dockwa_pay"
import { ManualCreditCard } from "./payment_flows/manual_credit_card"
import { PayLater } from "./payment_flows/pay_later"
import { SavedPaymentMethod } from "./payment_flows/saved_payment_method"

const PaymentMethodPanel = ({ launchPaymentFlow, showHeader = true }) => {
  const {
    cartController: {
      contact,
      totals: { total },
      cartStatus,
      setContact,
      setDockwaPayPaymentMethod,
      dockwaPayPaymentMethod,
    },
    dockwaPayModalOpen,
    setDockwaPayModalOpen,
  } = useContext(POSContext)
  const { sale: retrySale, noCardReadersConfigured } =
    useContext(CheckoutContext)
  const { isLargeScreen } = useWindowSize()

  const isRetry = !!retrySale
  const isCompleted = cartStatus === "completed"
  const isGuestSale = contact === null
  const zeroTotalCart = new Decimal(total).toNumber() === 0
  const cardReaderDisabledMsg = noCardReadersConfigured
    ? "Configure a card reader in your settings to enable this payment method."
    : null
  const zeroTotalCartDisabledMsg = zeroTotalCart
    ? "Payment not accepted for a $0 sale."
    : null

  function getCashDisabledMessage() {
    return isRetry && retrySale?.tip
      ? "Cash payment cannot be used to retry a sale with a tip."
      : zeroTotalCartDisabledMsg
  }

  function getCardReaderDisabledMessage() {
    return cardReaderDisabledMsg || zeroTotalCartDisabledMsg
  }

  function getCheckDisabledMessage() {
    return isRetry && retrySale?.tip
      ? "Check payment cannot be used to retry a sale with a tip."
      : zeroTotalCartDisabledMsg
  }

  function getPayLaterDisabledMessage() {
    return isRetry || isCompleted
      ? "Pay Later cannot be used to retry a sale."
      : null
  }

  function getSavedPaymentDisabledMessage() {
    return isCompleted && isGuestSale
      ? "Saved Payment cannot be used to retry a guest sale."
      : zeroTotalCartDisabledMsg
  }

  function getManualCreditCardDisabledMessage() {
    return zeroTotalCartDisabledMsg
  }

  return (
    <PanelWrapper>
      {showHeader ? (
        <div className="mb-4">
          <Heading.SectionHeader>Payment Method</Heading.SectionHeader>
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-2 rounded bg-gray-100 p-5 md:grid-cols-3 md:gap-6">
        <PaymentMethodButton
          disabledMessage={getCardReaderDisabledMessage()}
          name={CreditCard.paymentFlowName}
          amplitudePaymentMethodName={CreditCard.paymentFlowName}
          icon="fa-pos-credit-card"
          onClick={() =>
            launchPaymentFlow({
              paymentFlow: CreditCard,
            })
          }
        />
        <PaymentMethodButton
          disabledMessage={getSavedPaymentDisabledMessage()}
          name={SavedPaymentMethod.paymentFlowName}
          amplitudePaymentMethodName={SavedPaymentMethod.paymentFlowName}
          icon="fa-pos-saved-payment"
          onClick={() =>
            launchPaymentFlow({
              paymentFlow: SavedPaymentMethod,
            })
          }
        />
        <PaymentMethodButton
          disabledMessage={getCashDisabledMessage()}
          name={Cash.paymentFlowName}
          amplitudePaymentMethodName={Cash.paymentFlowName}
          icon="fa-pos-cash"
          onClick={() =>
            launchPaymentFlow({
              paymentFlow: Cash,
            })
          }
        />
        <PaymentMethodButton
          disabledMessage={getCheckDisabledMessage()}
          name={Check.paymentFlowName}
          amplitudePaymentMethodName={Check.paymentFlowName}
          icon="fa-pos-check"
          onClick={() =>
            launchPaymentFlow({
              paymentFlow: Check,
            })
          }
        />
        <PaymentMethodButton
          disabledMessage={getPayLaterDisabledMessage()}
          name={PayLater.paymentFlowName}
          amplitudePaymentMethodName={PayLater.paymentFlowName}
          icon="fa-pos-pay-later"
          onClick={() =>
            launchPaymentFlow({
              paymentFlow: PayLater,
            })
          }
        />
        <PaymentMethodButton
          disabledMessage={getManualCreditCardDisabledMessage()}
          name={ManualCreditCard.paymentFlowName}
          amplitudePaymentMethodName={ManualCreditCard.paymentFlowName}
          icon="fa-pos-manual-credit-card"
          onClick={() =>
            launchPaymentFlow({
              paymentFlow: ManualCreditCard,
            })
          }
        />
        <PaymentMethodButton
          disabledMessage={getSavedPaymentDisabledMessage()}
          name={
            dockwaPayPaymentMethod && isLargeScreen
              ? "Continue with Dockwa Pay"
              : "Scan Dockwa Pay QR Code"
          }
          amplitudePaymentMethodName={DockwaPay.paymentFlowName}
          subtitle={
            dockwaPayPaymentMethod && isLargeScreen
              ? dockwaPayPaymentMethod.title
              : null
          }
          icon="fa-qr-code"
          onClick={() => {
            dockwaPayPaymentMethod && isLargeScreen
              ? launchPaymentFlow({
                  paymentFlow: DockwaPay,
                })
              : setDockwaPayModalOpen(true)
          }}
        />
      </div>
      {dockwaPayModalOpen && isLargeScreen ? (
        <DockwaPayModal
          onClose={() => setDockwaPayModalOpen(false)}
          handleSuccess={({ contact, paymentMethod }) => {
            setContact(contact)
            setDockwaPayPaymentMethod(paymentMethod)
            launchPaymentFlow({
              paymentFlow: DockwaPay,
            })
          }}
        />
      ) : null}
    </PanelWrapper>
  )
}
PaymentMethodPanel.propTypes = {
  launchPaymentFlow: PropTypes.func.isRequired,
  showHeader: PropTypes.bool,
}

export default PaymentMethodPanel
